import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

function SettingsDialog({ isOpen, onClose, settings, onSettingsChange, onProcessAll, savedPresets = [] }) {
  const { theme, setLightTheme, setDarkTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('appearance');

  if (!isOpen) return null;

  const handleUpdateModeChange = (mode) => {
    onSettingsChange({ updateMode: mode });
  };

  const handleBatchSizeChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      onSettingsChange({ batchSize: value });
    }
  };

  const handleAutoOptimizeSettingChange = (setting, value) => {
    onSettingsChange({
      autoOptimize: {
        ...settings.autoOptimize,
        [setting]: value,
      },
    });
  };

  const tabs = [
    { id: 'appearance', label: 'Appearance', icon: '🎨' },
    { id: 'processing', label: 'Processing', icon: '⚙️' },
    { id: 'auto-optimize', label: 'Auto-Optimize', icon: '✨' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            Settings
          </h2>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 flex-shrink-0">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                <span className="mr-1.5">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6 overflow-y-auto flex-1 min-h-[400px]">
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Theme:
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={setLightTheme}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                    theme === 'light'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400 dark:hover:border-indigo-500'
                  }`}
                >
                  ☀️ Light
                </button>
                <button
                  onClick={setDarkTheme}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                    theme === 'dark'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400 dark:hover:border-indigo-500'
                  }`}
                >
                  🌙 Dark
                </button>
              </div>
            </div>
          )}

          {/* Processing Tab */}
          {activeTab === 'processing' && (
            <>
              {/* Update Mode Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  When Filter Settings Change:
                </h3>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="updateMode"
                  value="lazy"
                  checked={settings.updateMode === 'lazy'}
                  onChange={() => handleUpdateModeChange('lazy')}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                    Lazy (On-Demand)
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Process each page only when viewed (fastest, recommended)
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="updateMode"
                  value="batch"
                  checked={settings.updateMode === 'batch'}
                  onChange={() => handleUpdateModeChange('batch')}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                    Batch Processing
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Process pages in groups as you navigate
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="updateMode"
                  value="all"
                  checked={settings.updateMode === 'all'}
                  onChange={() => handleUpdateModeChange('all')}
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                    Process All Pages
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Reprocess all pages immediately (slowest for large PDFs)
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Batch Size Setting (only shown for batch mode) */}
          {settings.updateMode === 'batch' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Batch Size (pages per group)
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.batchSize}
                onChange={handleBatchSizeChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Process {settings.batchSize} page{settings.batchSize !== 1 ? 's' : ''} at a time
              </p>
            </div>
          )}

          {/* Process All Button */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              onClick={() => {
                onProcessAll();
                onClose();
              }}
              className="w-full px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors font-medium"
            >
              Process All Pages Now
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Manually process all pages with current filter settings
            </p>
          </div>
            </>
          )}

          {/* Auto-Optimize Tab */}
          {activeTab === 'auto-optimize' && (
            <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Auto-Optimize Options:
            </h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={settings.autoOptimize?.allowPreserve !== false}
                  onChange={(e) => handleAutoOptimizeSettingChange('allowPreserve', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 rounded"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 text-sm">
                    Allow "Preserve Original" suggestion
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Suggest keeping pages unchanged if already optimized
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={settings.autoOptimize?.requireReview !== false}
                  onChange={(e) => handleAutoOptimizeSettingChange('requireReview', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 rounded"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 text-sm">
                    Require review before applying
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Show preview dialog for approval
                  </p>
                </div>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Minimum ink savings threshold: {settings.autoOptimize?.minSavingsPercent || 10}%
                </label>
                <input
                  type="range"
                  min="5"
                  max="30"
                  step="5"
                  value={settings.autoOptimize?.minSavingsPercent || 10}
                  onChange={(e) => handleAutoOptimizeSettingChange('minSavingsPercent', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Only suggest optimizations with at least this much savings
                </p>
              </div>

              {/* Preset Selection for Auto-Optimize */}
              {savedPresets && savedPresets.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Consider these presets as optimization options:
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {savedPresets.map((preset) => (
                      <label key={preset.id} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={settings.autoOptimize?.enabledPresets?.includes(preset.id) ?? false}
                          onChange={(e) => {
                            const currentPresets = settings.autoOptimize?.enabledPresets || [];
                            const newPresets = e.target.checked
                              ? [...currentPresets, preset.id]
                              : currentPresets.filter(id => id !== preset.id);
                            handleAutoOptimizeSettingChange('enabledPresets', newPresets);
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 truncate">
                          {preset.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Auto-optimize will consider applying these presets when analyzing pages
                  </p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-800 transition-colors font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettingsDialog;
